import {
  searchContactReport,
  type ContactSearchParams,
} from '@ltvco/refresh-lib/v1';
import { useCallback } from 'react';

import { constants } from 'appConstants';

import type { ContactTeaserParamsForHandler } from 'components/ChatBV/utils/conversationUtils';

/** Maps chat handler params to ContactSearchParams (level as string[] per API). Trims strings so whitespace-only values are not sent. */
function toContactSearchParams(
  params: ContactTeaserParamsForHandler
): ContactSearchParams {
  const result: ContactSearchParams = {
    name: params.name?.trim() ?? '',
  };
  if (params.title?.trim()) {
    result.title = params.title.trim();
  }
  if (params.company?.trim()) {
    result.company = params.company.trim();
  }
  if (params.city?.trim()) {
    result.city = params.city.trim();
  }
  if (params.state?.trim()) {
    result.state = params.state.trim();
  }
  if (params.industry?.trim()) {
    result.industry = params.industry.trim();
  }
  if (params.level?.trim()) {
    result.level = [params.level.trim()];
  }
  return result;
}

/**
 * Returns searchContact for handleFunctionCalls (professional/contact teaser).
 * Uses searchContactReport from lib with app constants for base URL.
 */
export function useContactSearchOption(): (
  params: ContactTeaserParamsForHandler
) => Promise<{ results: unknown[]; count: number }> {
  const empty = { results: [] as unknown[], count: 0 };

  return useCallback(
    async (
      params: ContactTeaserParamsForHandler
    ): Promise<{ results: unknown[]; count: number }> => {
      if (!params.name?.trim()) {
        return empty;
      }
      const baseFromConstants =
        constants.links.brandLink?.trim() ||
        constants.links.baseUrl?.trim() ||
        '';
      // API often lives at origin (e.g. /hk/dd/...); baseUrl may include SPA path (e.g. /refresh)
      // so requests to baseUrl + path return HTML. Use origin only when base has a path.
      let apiBase = baseFromConstants || '/';
      try {
        const url = new URL(apiBase);
        if (url.pathname !== '/' && url.pathname !== '') {
          apiBase = url.origin;
        }
      } catch {
        // keep apiBase as-is
      }
      try {
        const contactSearchParams: ContactSearchParams = {
          ...toContactSearchParams(params),
          limit: '100',
        };
        const raw = await searchContactReport({ contactSearchParams }, apiBase);
        const totalCount = raw?.meta?.counts?.people ?? 0;
        const contacts = Array.isArray(
          (raw as { contacts?: unknown[] }).contacts
        )
          ? (raw as { contacts: unknown[] }).contacts
          : Array.isArray(
              (raw as { entities?: { people?: unknown[] } }).entities?.people
            )
          ? (raw as { entities: { people: unknown[] } }).entities.people
          : [];
        return { results: contacts, count: totalCount };
      } catch {
        return empty;
      }
    },
    []
  );
}
